/**
 * Erros das edge functions administrativas.
 *
 * O servidor responde recusas como `{ codigo, detail }` (403/409) e validações
 * como `{ codigo, detail, erros: { campo: mensagem } }` (422). A tela precisa do
 * CÓDIGO para reagir (mfa_requerido, proprio_acesso, ultimo_superadmin,
 * segregacao_funcoes) — nunca do texto.
 */

export class ErroAdmin extends Error {
  codigo: string | null;

  status: number;

  constructor(mensagem: string, codigo: string | null = null, status = 0) {
    super(mensagem);
    this.name = 'ErroAdmin';
    this.codigo = codigo;
    this.status = status;
  }
}

export interface ErroNormalizado {
  mensagem: string;
  codigo: string | null;
  status: number;
}

/** Lê o corpo da resposta de erro sem mascarar o código devolvido pelo servidor. */
export async function lerErroFuncao(error: unknown): Promise<ErroNormalizado> {
  let mensagem = 'Não foi possível concluir agora. Tente novamente.';
  let codigo: string | null = null;
  let status = 0;

  const ctx = (error as { context?: Response }).context;
  if (ctx) {
    status = ctx.status ?? 0;
    if (typeof ctx.json === 'function') {
      try {
        const corpo = (await ctx.json()) as {
          codigo?: string;
          detail?: string;
          error?: string;
          erros?: Record<string, string>;
        } | null;
        if (corpo?.codigo) codigo = corpo.codigo;
        if (corpo?.error) mensagem = corpo.error;
        else if (corpo?.detail) {
          const campos = Object.values(corpo.erros ?? {}).filter(Boolean);
          mensagem = campos.length > 0 ? `${corpo.detail} ${campos.join(' · ')}` : corpo.detail;
        }
      } catch {
        /* mantém a mensagem padrão */
      }
    }
  }

  return { mensagem, codigo, status };
}

/** Avisa a aplicação que a sessão perdeu o segundo fator e precisa voltar ao código. */
export function avisarMfaRequerido(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('petbea:mfa_requerido'));
}

/** Converte o erro em ErroAdmin, disparando o aviso global de MFA quando for o caso. */
export async function lancarErroAdmin(error: unknown): Promise<never> {
  const info = await lerErroFuncao(error);
  if (info.codigo === 'mfa_requerido') avisarMfaRequerido();
  throw new ErroAdmin(info.mensagem, info.codigo, info.status);
}

/** Código de erro de um ErroAdmin (ou null). */
export function codigoDoErro(e: unknown): string | null {
  return e instanceof ErroAdmin ? e.codigo : null;
}

/** Mensagem legível de qualquer erro. */
export function mensagemDoErro(e: unknown, padrao = 'Não foi possível concluir agora.'): string {
  return e instanceof Error && e.message ? e.message : padrao;
}